import type { Class, Weekday } from '@pirouette/shared';
import { cn } from '@/lib/cn';

interface WeeklyGridProps {
  classes: Class[];
  conflictedClassIds?: ReadonlySet<string>;
  onSelect?: (classId: string) => void;
}

const WEEKDAYS: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const WEEKDAY_LABEL: Record<Weekday, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

// Hours rendered on the grid. Studio hours 14:00 → 21:00 cover after-school
// + evening; bump if the studio runs morning classes.
const HOUR_START = 14;
const HOUR_END = 21;
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

function timeToOffset(time: string): number {
  // 'HH:MM' → fractional hour from HOUR_START. Clamped at 0.
  const [h, m] = time.split(':').map(Number);
  if (h === undefined || m === undefined) return 0;
  return Math.max(0, h + m / 60 - HOUR_START);
}

export function WeeklyGrid({ classes, conflictedClassIds, onSelect }: WeeklyGridProps) {
  return (
    <div role="region" aria-label="Weekly schedule grid" className="surface overflow-hidden">
      <div
        className="grid"
        style={{
          gridTemplateColumns: '4rem repeat(7, minmax(0, 1fr))',
          gridTemplateRows: `2.5rem repeat(${HOURS.length * 2}, 1.5rem)`,
        }}
      >
        {/* Header row */}
        <div className="border-b border-[var(--color-border)]" aria-hidden="true" />
        {WEEKDAYS.map((d) => (
          <div
            key={d}
            className="flex items-center justify-center border-b border-l border-[var(--color-border)] font-medium text-[var(--color-ink-muted)] text-[var(--text-sm)]"
          >
            {WEEKDAY_LABEL[d]}
          </div>
        ))}

        {/* Hour labels — each hour spans 2 rows (every 30 min gridline). */}
        {HOURS.map((h) => (
          <div
            key={`label-${h}`}
            className="row-span-2 border-t border-[var(--color-border)] pt-0.5 pr-2 text-right text-[var(--color-ink-subtle)] text-[var(--text-xs)] tabular-nums"
            aria-hidden="true"
          >
            {String(h).padStart(2, '0')}:00
          </div>
        ))}

        {/* Empty cells creating the grid lines. */}
        {WEEKDAYS.map((d) =>
          HOURS.map((h) => (
            <div
              key={`cell-${d}-${h}`}
              className="row-span-2 border-t border-l border-[var(--color-border)]"
              aria-hidden="true"
            />
          )),
        )}

        {/* Classes overlaid */}
        {classes.map((c) => {
          const col = WEEKDAYS.indexOf(c.weekday);
          if (col < 0) return null;
          const top = timeToOffset(c.start_time);
          const height = Math.max(timeToOffset(c.end_time) - top, 0.5); // min 30 minutes
          const conflicted = conflictedClassIds?.has(c.id) ?? false;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect?.(c.id)}
              aria-label={`${c.name}, ${WEEKDAY_LABEL[c.weekday]} ${c.start_time}–${c.end_time}, ${c.room}${conflicted ? ' — has conflict' : ''}`}
              className={cn(
                // Grid placement is set via inline `style` below — the
                // col-start/row-start values are dynamic per class, so
                // Tailwind's JIT can't statically extract them.
                'relative m-0.5 rounded-[var(--radius-md)] p-2 text-left text-[var(--text-xs)] transition-colors',
                'focus-visible:outline-none',
                conflicted
                  ? 'bg-[var(--color-danger-muted)] ring-1 ring-[var(--color-danger)] hover:bg-[var(--color-danger-muted)]'
                  : 'bg-[var(--color-accent-muted)] hover:bg-[var(--color-surface-sunken)]',
              )}
              style={{
                gridColumnStart: col + 2, // +1 for hour label, +1 for 1-index
                gridRowStart: Math.floor(top * 2) + 2, // +1 for header row, +1 for 1-index
                gridRowEnd: `span ${Math.max(1, Math.ceil(height * 2))}`,
              }}
            >
              <p className="truncate font-medium text-[var(--color-ink)]">{c.name}</p>
              <p className="truncate text-[var(--color-ink-muted)]">
                {c.start_time}–{c.end_time} · {c.room}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
